/**
 * The inspector for the selection: position and size, layout (auto-size boxes, stacks, padding), anchoring,
 * typography and text overflow, fill/stroke/corner radius/shadow, images, and "Expose as field".
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { Eye, EyeOff, Link2, Lock, LockOpen, Unlink } from 'lucide-react';
import type { Element, Fill, GroupElement, ShapeStyle, TextElement } from '@prism-graphics/scene-schema';
import type { StaticFrame } from '@prism-graphics/runtime/static';
import { t } from '../i18n.ts';
import { ensureAsset, patchElement, pruneAssets, setAnchor, setCanvas, setMeta, setSize, modelGeo, type Geo } from '../model/commands.ts';
import { geoFromFrame } from '../canvas/geo.ts';
import { brandCache } from '../state/brand.ts';
import { BLEND_MODES, layoutsOf } from '@prism-graphics/scene-schema';
import { exposableProperties, fieldFor, unexposeField, updateField, type Exposable } from '../model/fields.ts';
import { findElement, parentOf } from '../model/tree.ts';
import { alignSelection, distributeSelection, expose, toggleLocked, toggleVisible } from '../state/actions.ts';
import { assetCache } from '../state/assets.ts';
import { activeLayout, sceneView, useEditor, type EditorStore } from '../state/store.ts';
import { overriddenKeys, primaryOf, type OverrideKey } from '../model/layouts.ts';
import { ColorField, IconButton, NumberField, Row, Section, SelectField, TextField, Toggle } from '../ui/controls.tsx';
import { AnimationSection } from '../timeline/AnimationSection.tsx';
import { shapeLabel } from '../ui/formats.tsx';

type Edit = (fn: (el: Element) => Element, key: string) => void;

/** The active layout when it is not the primary (ADR 0037): its label and what it overrides of an element. */
function useLayoutOverrides(store: EditorStore, elementId: string): { label: string; keys: Set<OverrideKey> } | undefined {
  const doc = useEditor(store, (s) => s.history.present);
  const layout = useEditor(store, activeLayout);
  if (layout === primaryOf(doc)) return undefined;
  return { label: layoutsOf(doc).find((l) => l.id === layout)?.label ?? layout, keys: overriddenKeys(doc, layout, elementId) };
}

/** A dot and "Differs in 9:16 · Reset to primary" when the active layout overrides any of `keys` of the element. */
function OverrideReset({ store, elementId, keys, testId }: { store: EditorStore; elementId: string; keys: (k: OverrideKey) => boolean; testId: string }) {
  const o = useLayoutOverrides(store, elementId);
  const hit = o ? [...o.keys].filter(keys) : [];
  if (!o || hit.length === 0) return null;
  return (
    <div className="override-row" data-testid={`override-${testId}`}>
      <span className="override-dot" aria-hidden />
      <span>{t('layouts.overridden', { label: o.label })}</span>
      <button type="button" className="btn small" title={t('layouts.resetHint')} data-testid={`reset-${testId}`} onClick={() => store.resetOverrides(elementId, hit)}>
        {t('layouts.reset')}
      </button>
    </div>
  );
}

/** Editing a layout other than the primary: what applies to it only, and "Show in this layout". */
function LayoutNote({ store, el }: { store: EditorStore; el: Element }) {
  const o = useLayoutOverrides(store, el.id);
  if (!o) return null;
  return (
    <div className="layout-note" data-testid="layout-note">
      <p className="hint">{t('layouts.editing', { label: o.label })}</p>
      <Toggle label={t('layouts.showHere')} checked={el.visible} testId="show-in-layout" onChange={(visible) => store.apply((s) => patchElement(s, el.id, { visible } as Partial<Element>))} />
      <OverrideReset store={store} elementId={el.id} keys={(k) => k === 'visible'} testId="visible" />
    </div>
  );
}

export function Inspector({ store, frame }: { store: EditorStore; frame: StaticFrame | undefined }) {
  const scene = useEditor(store, sceneView);
  const selection = useEditor(store, (s) => s.selection);
  if (selection.length === 0) return <GraphicSettings store={store} />;
  if (selection.length > 1) return <MultiSelection store={store} count={selection.length} />;
  const el = findElement(scene, selection[0]!);
  if (!el) return null;
  return <ElementInspector key={el.id} store={store} el={el} frame={frame} />;
}

function GraphicSettings({ store }: { store: EditorStore }) {
  const scene = useEditor(store, sceneView);
  // In another layout than the primary the size changes with "Change format…" only (a new aspect, re-adapted).
  const inPrimary = useEditor(store, (s) => activeLayout(s) === primaryOf(s.history.present));
  const bg = scene.canvas.background;
  useSyncExternalStore(assetCache.subscribe, assetCache.getVersion);
  const images = assetCache.library.filter((a) => a.kind === 'image');
  return (
    <div className="inspector" data-testid="inspector">
      <h2 className="panel-title">{t('inspector.graphic')}</h2>
      <Section title={t('inspector.graphic.meta')}>
        <TextField label={t('inspector.graphic.name')} value={scene.meta.name} onChange={(name) => name.trim() && store.apply((s) => setMeta(s, { name }), { key: 'meta.name' })} />
        <TextField label={t('inspector.graphic.description')} multiline value={scene.meta.description ?? ''} onChange={(description) => store.apply((s) => setMeta(s, { description: description || undefined }), { key: 'meta.description' })} />
        <p className="hint">{t('inspector.graphic.id', { id: scene.meta.id })}</p>
      </Section>
      <Section title={t('inspector.graphic.canvas')}>
        {inPrimary ? (
          <Row className="two">
            <NumberField label={t('inspector.width')} short="W" value={scene.canvas.width} min={16} max={7680} onChange={(width) => store.apply((s) => setCanvas(s, { width: Math.round(width) }), { key: 'canvas.w' })} />
            <NumberField label={t('inspector.height')} short="H" value={scene.canvas.height} min={16} max={4320} onChange={(height) => store.apply((s) => setCanvas(s, { height: Math.round(height) }), { key: 'canvas.h' })} />
          </Row>
        ) : (
          <p className="hint" data-testid="layout-canvas">
            {t('layouts.canvasHint', { width: scene.canvas.width, height: scene.canvas.height })}
          </p>
        )}
        <p className="hint" data-testid="canvas-shape">
          {shapeLabel(scene.canvas)}
        </p>
        <p className="hint">{t('inspector.graphic.rate', { num: scene.canvas.frameRate.num, den: scene.canvas.frameRate.den })}</p>
        <button type="button" className="btn small" onClick={() => store.setUi({ formatDialog: { width: scene.canvas.width, height: scene.canvas.height }, formatDialogMode: 'change' })} data-testid="change-format">
          {t('format.change')}
        </button>
      </Section>
      <Section title={t('inspector.graphic.reference')}>
        <p className="hint">{t('inspector.graphic.referenceHint')}</p>
        <Toggle
          label={t('inspector.graphic.referenceColor')}
          checked={bg?.color !== undefined}
          onChange={(on) => store.apply((s) => setCanvas(s, { background: on ? { ...(bg ?? {}), color: '#3A4A55' } : bg?.asset ? { asset: bg.asset } : undefined }))}
        />
        {bg?.color !== undefined && <ColorField label={t('inspector.color')} value={bg.color} onChange={(color) => store.apply((s) => setCanvas(s, { background: { ...bg, color } }), { key: 'bg.color' })} />}
        <SelectField
          label={t('inspector.graphic.referenceImage')}
          value={bg?.asset ?? ''}
          options={[{ value: '', label: t('inspector.none') }, ...images.map((a) => ({ value: a.hash, label: a.originalName }))]}
          onChange={(hash) => {
            const a = images.find((x) => x.hash === hash);
            store.apply((s) => {
              const next = a ? ensureAsset(s, a.sceneAsset) : s;
              const background = { ...(bg?.color ? { color: bg.color } : {}), ...(a ? { asset: a.hash } : {}) };
              return setCanvas(next, { background: Object.keys(background).length ? background : undefined });
            });
          }}
        />
      </Section>
      <p className="hint pad">{t('inspector.nothingSelected')}</p>
    </div>
  );
}

function MultiSelection({ store, count }: { store: EditorStore; count: number }) {
  return (
    <div className="inspector" data-testid="inspector">
      <h2 className="panel-title">{t('inspector.multi', { count })}</h2>
      <Section title={t('toolbar.align')}>
        <div className="btn-grid">
          {(['left', 'hcenter', 'right', 'top', 'vmiddle', 'bottom'] as const).map((a) => (
            <button key={a} type="button" className="btn small" onClick={() => alignSelection(store, a)}>
              {t(`align.${a}`)}
            </button>
          ))}
          <button type="button" className="btn small" disabled={count < 3} onClick={() => distributeSelection(store, 'x')}>
            {t('align.distributeH')}
          </button>
          <button type="button" className="btn small" disabled={count < 3} onClick={() => distributeSelection(store, 'y')}>
            {t('align.distributeV')}
          </button>
        </div>
      </Section>
      <Section title={t('inspector.visibility')}>
        <div className="btn-grid">
          <button type="button" className="btn small" onClick={() => toggleVisible(store)}>
            {t('layers.hide')} / {t('layers.show')}
          </button>
          <button type="button" className="btn small" onClick={() => toggleLocked(store)}>
            {t('layers.lock')} / {t('layers.unlock')}
          </button>
        </div>
      </Section>
    </div>
  );
}

const TYPE_LABEL: Record<Element['type'], string> = {
  text: 'type.text',
  rect: 'type.rect',
  ellipse: 'type.ellipse',
  line: 'type.line',
  image: 'type.image',
  video: 'type.video',
  group: 'type.group',
  clipMask: 'type.clipMask',
};

function ElementInspector({ store, el, frame }: { store: EditorStore; el: Element; frame: StaticFrame | undefined }) {
  const scene = useEditor(store, sceneView);
  const timelineOpen = useEditor(store, (s) => s.ui.bottomTab === 'timeline');
  const edit: Edit = (fn, key) => store.apply((s) => patchElement(s, el.id, fn), { key: `${el.id}:${key}` });
  const parent = parentOf(scene, el.id);
  const inStack = parent?.type === 'group' && parent.layout.mode === 'stack';
  const g = frame?.geometry.get(el.id);
  const label = el.type === 'group' && el.layout.mode === 'stack' ? (el.style.fill ? 'type.box' : 'type.stack') : TYPE_LABEL[el.type];
  return (
    <div className="inspector" data-testid="inspector" data-element-id={el.id}>
      <div className="inspector-head">
        <input
          className="el-name"
          aria-label={t('inspector.name')}
          value={el.name}
          data-testid="element-name"
          onChange={(e) => edit((x) => ({ ...x, name: e.target.value }) as Element, 'name')}
        />
        <span className="badge">{t(label)}</span>
        <IconButton label={el.locked ? t('layers.unlock') : t('layers.lock')} pressed={el.locked} onClick={() => toggleLocked(store, [el.id])}>
          {el.locked ? <Lock size={14} /> : <LockOpen size={14} />}
        </IconButton>
        <IconButton label={el.visible ? t('layers.hide') : t('layers.show')} pressed={!el.visible} onClick={() => toggleVisible(store, [el.id])}>
          {el.visible ? <Eye size={14} /> : <EyeOff size={14} />}
        </IconButton>
      </div>
      <LayoutNote store={store} el={el} />
      {timelineOpen && <AnimationSection store={store} el={el} />}
      <PositionSection el={el} edit={edit} store={store} inStack={inStack} nested={parent !== undefined} geo={frame ? geoFromFrame(scene, frame) : modelGeo(scene)} measured={g ? { w: g.box.width, h: g.box.height } : undefined} />
      {(el.type === 'group' || el.type === 'clipMask' || el.type === 'text') && <SizeRules el={el} store={store} measured={g ? { w: g.box.width, h: g.box.height } : undefined} />}
      {el.type === 'group' && <LayoutSection el={el} edit={edit} />}
      {el.type === 'clipMask' && (
        <Section title={t('inspector.mask')} testId="section-mask">
          <SelectField label={t('inspector.mask.shape')} value={el.shape} testId="mask-shape" options={[{ value: 'rect', label: t('type.rect') }, { value: 'ellipse', label: t('type.ellipse') }]} onChange={(shape) => edit((x) => ({ ...x, shape }) as Element, 'shape')} />
          {el.shape === 'rect' && (
            <NumberField
              label={t('inspector.radius')}
              short="r"
              value={typeof el.cornerRadius === 'number' ? el.cornerRadius : (el.cornerRadius?.[0] ?? 0)}
              min={0}
              testId="mask-radius"
              onChange={(v) => edit((x) => (x.type === 'clipMask' ? ({ ...x, cornerRadius: v || undefined } as Element) : x), 'maskRadius')}
            />
          )}
          <Toggle label={t('inspector.mask.fixedContent')} checked={el.fixedContent ?? false} testId="mask-fixed" onChange={(on) => edit((x) => (x.type === 'clipMask' ? ({ ...x, fixedContent: on || undefined } as Element) : x), 'maskFixed')} />
          <p className="hint">{t('inspector.mask.hint')}</p>
        </Section>
      )}
      {el.type === 'text' && <TextSection el={el} edit={edit} store={store} />}
      {el.type === 'text' && <OverflowSection el={el} edit={edit} fit={frame?.text.find((x) => x.id === el.id)} />}
      {(el.type === 'rect' || el.type === 'ellipse' || el.type === 'group') && <ShapeSection el={el} edit={edit} />}
      {el.type === 'line' && <LineSection el={el} edit={edit} />}
      {(el.type === 'image' || el.type === 'video') && <MediaSection el={el} edit={edit} store={store} />}
      <EffectsSection el={el} edit={edit} />
      <FieldsSection el={el} store={store} />
    </div>
  );
}

function PositionSection({ el, edit, store, inStack, nested, geo, measured }: { el: Element; edit: Edit; store: EditorStore; inStack: boolean; nested: boolean; geo: Geo; measured: { w: number; h: number } | undefined }) {
  const tf = el.transform;
  const setT = (patch: Partial<Element['transform']>, key: string) => edit((x) => ({ ...x, transform: { ...x.transform, ...patch } }) as Element, key);
  const wAuto = el.size.width === 'auto';
  const hAuto = el.size.height === 'auto';
  const anchor = el.anchor ?? { horizontal: 'left', vertical: 'top', to: 'parent' };
  return (
    <Section title={t('inspector.position')} testId="section-position">
      {inStack && <p className="hint">{t('inspector.inStack')}</p>}
      <Row className="two">
        <NumberField label={t('inspector.x')} short="X" value={tf.x} onChange={(x) => setT({ x }, 'x')} testId="prop-x" />
        <NumberField label={t('inspector.y')} short="Y" value={tf.y} onChange={(y) => setT({ y }, 'y')} testId="prop-y" />
      </Row>
      <Row className="two">
        <NumberField
          label={t('inspector.width')}
          short="W"
          value={wAuto ? measured?.w : (el.size.width as number)}
          placeholder={t('inspector.auto')}
          min={1}
          testId="prop-w"
          onChange={(w) => store.apply((s) => setSize(s, el.id, { width: w }), { key: `${el.id}:w` })}
        />
        <NumberField
          label={el.type === 'line' ? t('inspector.thickness') : t('inspector.height')}
          short="H"
          value={el.type === 'line' ? el.stroke.width : hAuto ? measured?.h : (el.size.height as number)}
          min={el.type === 'line' ? 0 : 1}
          testId="prop-h"
          onChange={(h) =>
            el.type === 'line'
              ? edit((x) => (x.type === 'line' ? { ...x, stroke: { ...x.stroke, width: h } } : x), 'stroke.width')
              : store.apply((s) => setSize(s, el.id, { height: h }), { key: `${el.id}:h` })
          }
        />
      </Row>
      <Row className="two">
        <NumberField label={t('inspector.rotation')} short="°" value={tf.rotation} step={1} onChange={(rotation) => setT({ rotation }, 'rotation')} />
        <NumberField label={t('inspector.opacity')} short="α" value={Math.round(tf.opacity * 100)} min={0} max={100} suffix="%" onChange={(o) => setT({ opacity: o / 100 }, 'opacity')} />
      </Row>
      <Row className="two">
        <NumberField label={t('inspector.scaleX')} short="SX" value={tf.scaleX} step={0.05} onChange={(scaleX) => setT({ scaleX }, 'scaleX')} />
        <NumberField label={t('inspector.scaleY')} short="SY" value={tf.scaleY} step={0.05} onChange={(scaleY) => setT({ scaleY }, 'scaleY')} />
      </Row>
      {!inStack && (
        <>
          <Row className="two">
            <SelectField
              label={t('inspector.anchorH')}
              value={anchor.horizontal}
              testId="anchor-h"
              options={[
                { value: 'left', label: t('anchor.left') },
                { value: 'center', label: t('anchor.center') },
                { value: 'right', label: t('anchor.right') },
                { value: 'stretch', label: t('anchor.stretch') },
              ]}
              onChange={(horizontal) => store.apply((s) => setAnchor(s, el.id, { ...anchor, horizontal }, geo), { key: `${el.id}:anchor` })}
            />
            <SelectField
              label={t('inspector.anchorV')}
              value={anchor.vertical}
              testId="anchor-v"
              options={[
                { value: 'top', label: t('anchor.top') },
                { value: 'middle', label: t('anchor.middle') },
                { value: 'bottom', label: t('anchor.bottom') },
                { value: 'stretch', label: t('anchor.stretch') },
              ]}
              onChange={(vertical) => store.apply((s) => setAnchor(s, el.id, { ...anchor, vertical }, geo), { key: `${el.id}:anchor` })}
            />
          </Row>
          {nested && (
            <SelectField
              label={t('inspector.anchorTo')}
              value={anchor.to}
              testId="anchor-to"
              options={[
                { value: 'parent', label: t('anchor.toParent') },
                { value: 'canvas', label: t('anchor.toCanvas') },
              ]}
              onChange={(to) => store.apply((s) => setAnchor(s, el.id, { ...anchor, to }, geo), { key: `${el.id}:anchorTo` })}
            />
          )}
          <p className="hint">{t('inspector.anchorHint')}</p>
        </>
      )}
      <OverrideReset store={store} elementId={el.id} keys={(k) => k.startsWith('transform.') || k === 'anchor' || k === 'size.width' || k === 'size.height'} testId="position" />
    </Section>
  );
}

function SizeRules({ el, store, measured }: { el: Element; store: EditorStore; measured: { w: number; h: number } | undefined }) {
  const z = el.size;
  const set = (patch: Partial<Element['size']>, key: string) => store.apply((s) => setSize(s, el.id, patch), { key: `${el.id}:${key}` });
  const opt = (v: number | undefined) => v;
  return (
    <Section title={t('inspector.sizeRules')} testId="section-size">
      <Toggle
        label={el.type === 'text' ? t('inspector.autoWidthText') : t('inspector.autoWidth')}
        checked={z.width === 'auto'}
        testId="auto-width"
        onChange={(on) => set({ width: on ? 'auto' : Math.round(measured?.w ?? 200) }, 'w')}
      />
      <Toggle label={t('inspector.autoHeight')} checked={z.height === 'auto'} testId="auto-height" onChange={(on) => set({ height: on ? 'auto' : Math.round(measured?.h ?? 100) }, 'h')} />
      <Row className="two">
        <NumberField label={t('inspector.minWidth')} short="min W" value={opt(z.minWidth)} min={0} placeholder="–" testId="min-width" onChange={(v) => set({ minWidth: v || undefined }, 'minW')} />
        <NumberField label={t('inspector.maxWidth')} short="max W" value={opt(z.maxWidth)} min={0} placeholder="–" testId="max-width" onChange={(v) => set({ maxWidth: v || undefined }, 'maxW')} />
      </Row>
      <Row className="two">
        <NumberField label={t('inspector.minHeight')} short="min H" value={opt(z.minHeight)} min={0} placeholder="–" onChange={(v) => set({ minHeight: v || undefined }, 'minH')} />
        <NumberField label={t('inspector.maxHeight')} short="max H" value={opt(z.maxHeight)} min={0} placeholder="–" onChange={(v) => set({ maxHeight: v || undefined }, 'maxH')} />
      </Row>
      <p className="hint">{el.type === 'text' ? t('inspector.sizeRulesTextHint') : t('inspector.sizeRulesHint')}</p>
      <OverrideReset store={store} elementId={el.id} keys={(k) => k.startsWith('size.min') || k.startsWith('size.max')} testId="size" />
    </Section>
  );
}

function LayoutSection({ el, edit }: { el: GroupElement; edit: Edit }) {
  const L = el.layout;
  const stack = L.stack ?? { direction: 'vertical' as const, gap: 0, align: 'start' as const, justify: 'start' as const };
  const pad = L.padding ?? { top: 0, right: 0, bottom: 0, left: 0 };
  const setL = (patch: Partial<GroupElement['layout']>, key: string) => edit((x) => (x.type === 'group' ? { ...x, layout: { ...x.layout, ...patch } } : x), key);
  return (
    <Section title={t('inspector.layout')} testId="section-layout">
      <SelectField
        label={t('inspector.layoutMode')}
        value={L.mode}
        testId="layout-mode"
        options={[
          { value: 'absolute', label: t('layout.absolute') },
          { value: 'stack', label: t('layout.stack') },
        ]}
        onChange={(mode) => setL({ mode, ...(mode === 'stack' && !L.stack ? { stack } : {}) }, 'mode')}
      />
      {L.mode === 'stack' && (
        <>
          <Row className="two">
            <SelectField
              label={t('inspector.direction')}
              value={stack.direction}
              testId="stack-direction"
              options={[
                { value: 'horizontal', label: t('layout.horizontal') },
                { value: 'vertical', label: t('layout.vertical') },
              ]}
              onChange={(direction) => setL({ stack: { ...stack, direction } }, 'direction')}
            />
            <NumberField label={t('inspector.gap')} short={t('inspector.gap')} value={stack.gap} min={0} testId="stack-gap" onChange={(gap) => setL({ stack: { ...stack, gap } }, 'gap')} />
          </Row>
          <Row className="two">
            <SelectField
              label={t('inspector.align')}
              value={stack.align}
              testId="stack-align"
              options={[
                { value: 'start', label: t('layout.start') },
                { value: 'center', label: t('layout.center') },
                { value: 'end', label: t('layout.end') },
                { value: 'stretch', label: t('layout.stretch') },
              ]}
              onChange={(align) => setL({ stack: { ...stack, align } }, 'align')}
            />
            <SelectField
              label={t('inspector.justify')}
              value={stack.justify}
              options={[
                { value: 'start', label: t('layout.start') },
                { value: 'center', label: t('layout.center') },
                { value: 'end', label: t('layout.end') },
                { value: 'space-between', label: t('layout.spaceBetween') },
              ]}
              onChange={(justify) => setL({ stack: { ...stack, justify } }, 'justify')}
            />
          </Row>
        </>
      )}
      <div className="field-label sub">{t('inspector.padding')}</div>
      <Row className="four">
        {(['top', 'right', 'bottom', 'left'] as const).map((side) => (
          <NumberField key={side} label={t(`inspector.padding.${side}`)} short={t(`inspector.padding.${side}.short`)} value={pad[side]} min={0} testId={`padding-${side}`} onChange={(v) => setL({ padding: { ...pad, [side]: v } }, `pad.${side}`)} />
        ))}
      </Row>
      <Toggle label={t('inspector.clip')} checked={el.clipContent} onChange={(clipContent) => edit((x) => ({ ...x, clipContent }) as Element, 'clip')} />
    </Section>
  );
}

function TextSection({ el, edit, store }: { el: TextElement; edit: Edit; store: EditorStore }) {
  const scene = useEditor(store, sceneView);
  const focusText = useEditor(store, (s) => s.focusText);
  useSyncExternalStore(assetCache.subscribe, assetCache.getVersion);
  const ref = useRef<HTMLTextAreaElement & HTMLInputElement>(null);
  useEffect(() => {
    if (focusText) {
      ref.current?.focus();
      ref.current?.select();
    }
  }, [focusText]);
  const s = el.style;
  const setS = (patch: Partial<TextElement['style']>, key: string) => edit((x) => (x.type === 'text' ? { ...x, style: { ...x.style, ...patch } } : x), key);
  const bound = fieldFor(scene, el.id, 'text');
  useSyncExternalStore(brandCache.subscribe, brandCache.getVersion);
  const fonts = new Map<string, string>();
  const label = (a: (typeof assetCache.library)[number]) => `${a.meta.family ?? a.originalName}${a.meta.subfamily && a.meta.subfamily !== 'Regular' ? ` ${a.meta.subfamily}` : ''}`;
  // Brand fonts first (ADR 0030), then the rest of the library.
  for (const h of brandCache.kit.fonts) {
    const a = assetCache.byHash(h);
    if (a?.kind === 'font') fonts.set(a.hash, `${label(a)} · ${t('font.brandGroup')}`);
  }
  for (const a of assetCache.library) if (a.kind === 'font' && !fonts.has(a.hash)) fonts.set(a.hash, label(a));
  for (const a of scene.assets) if (a.kind === 'font' && !fonts.has(a.hash)) fonts.set(a.hash, a.fileName);
  const fontAsset = scene.assets.find((a) => a.hash === s.font);
  const range = (fontAsset?.fontWeight ?? '100 900').split(/\s+/).map(Number);
  const weights = [100, 200, 300, 400, 500, 600, 700, 800, 900].filter((w) => w >= (range[0] ?? 100) && w <= (range[1] ?? range[0] ?? 900));
  if (!weights.includes(s.fontWeight)) weights.push(s.fontWeight);
  return (
    <Section title={t('inspector.text')} testId="section-text">
      <TextField
        label={bound ? t('inspector.textBound', { title: bound.title }) : t('inspector.textContent')}
        multiline
        rows={2}
        value={bound && bound.type === 'string' ? bound.default : el.text}
        testId="text-content"
        inputRef={ref}
        onChange={(text) => {
          if (bound) store.apply((sc) => updateField(sc, bound.id, { default: text }), { key: `${el.id}:text` });
          else edit((x) => ({ ...x, text }) as Element, 'text');
        }}
      />
      <SelectField
        label={t('inspector.font')}
        value={s.font}
        testId="font"
        options={[...fonts].map(([value, label]) => ({ value, label }))}
        onChange={(hash) => {
          const lib = assetCache.byHash(hash);
          store.apply((sc) => {
            let next = lib ? ensureAsset(sc, lib.sceneAsset) : sc;
            next = patchElement(next, el.id, (x) => (x.type === 'text' ? { ...x, style: { ...x.style, font: hash } } : x));
            return pruneAssets(next);
          });
        }}
      />
      <Row className="two">
        <SelectField label={t('inspector.weight')} value={String(s.fontWeight)} testId="font-weight" options={weights.sort((a, b) => a - b).map((w) => ({ value: String(w), label: t(`weight.${w}`) === `weight.${w}` ? String(w) : `${w} ${t(`weight.${w}`)}` }))} onChange={(v) => setS({ fontWeight: Number(v) }, 'weight')} />
        <NumberField label={t('inspector.fontSize')} short={t('inspector.size')} value={s.fontSize} min={1} suffix="px" testId="font-size" onChange={(fontSize) => setS({ fontSize }, 'size')} />
      </Row>
      <OverrideReset store={store} elementId={el.id} keys={(k) => k === 'fontSize'} testId="font-size" />
      <Row className="two">
        <NumberField label={t('inspector.tracking')} short={t('inspector.tracking.short')} value={s.letterSpacing} step={0.5} suffix="px" onChange={(letterSpacing) => setS({ letterSpacing }, 'tracking')} />
        <NumberField label={t('inspector.lineHeight')} short={t('inspector.lineHeight.short')} value={s.lineHeight} step={0.05} min={0.5} onChange={(lineHeight) => setS({ lineHeight }, 'lineHeight')} />
      </Row>
      <Row className="two">
        <SelectField
          label={t('inspector.case')}
          value={s.textTransform}
          options={[
            { value: 'none', label: t('case.none') },
            { value: 'uppercase', label: t('case.uppercase') },
            { value: 'lowercase', label: t('case.lowercase') },
            { value: 'capitalize', label: t('case.capitalize') },
          ]}
          onChange={(textTransform) => setS({ textTransform }, 'case')}
        />
        <SelectField
          label={t('inspector.textAlign')}
          value={s.textAlign}
          options={[
            { value: 'left', label: t('anchor.left') },
            { value: 'center', label: t('anchor.center') },
            { value: 'right', label: t('anchor.right') },
          ]}
          onChange={(textAlign) => setS({ textAlign }, 'align')}
        />
      </Row>
      <Toggle label={t('inspector.italic')} checked={s.fontStyle === 'italic'} onChange={(on) => setS({ fontStyle: on ? 'italic' : 'normal' }, 'italic')} />
      <SelectField
        label={t('inspector.fill')}
        value={s.fill?.type === 'linear-gradient' ? 'linear-gradient' : 'solid'}
        testId="text-fill-type"
        options={[
          { value: 'solid', label: t('fill.solid') },
          { value: 'linear-gradient', label: t('fill.gradient') },
        ]}
        onChange={(type) => setS({ fill: type === 'linear-gradient' ? { type, angle: 90, stops: [{ offset: 0, color: s.color }, { offset: 1, color: '#7FD8E4' }] } : undefined }, 'textFill')}
      />
      {s.fill?.type === 'linear-gradient' ? (
        <>
          <NumberField label={t('inspector.angle')} short="°" value={s.fill.angle} testId="text-gradient-angle" onChange={(angle) => s.fill?.type === 'linear-gradient' && setS({ fill: { ...s.fill, angle } }, 'textAngle')} />
          {s.fill.stops.map((stop, i) => (
            <ColorField
              key={i}
              label={t('inspector.stop', { n: i + 1 })}
              value={stop.color}
              testId={`text-stop-${i}`}
              onChange={(color) => s.fill?.type === 'linear-gradient' && setS({ fill: { ...s.fill, stops: s.fill.stops.map((s2, j) => (j === i ? { ...s2, color } : s2)) } }, `textStop${i}`)}
            />
          ))}
        </>
      ) : (
        <ColorField label={t('inspector.color')} value={s.color} testId="text-color" onChange={(color) => setS({ color }, 'color')} />
      )}
      <Toggle label={t('inspector.stroke')} checked={s.stroke !== undefined} onChange={(on) => setS({ stroke: on ? { color: '#000000', width: 2 } : undefined }, 'stroke')} />
      {s.stroke && (
        <Row className="two">
          <ColorField label={t('inspector.strokeColor')} value={s.stroke.color} onChange={(color) => setS({ stroke: { ...s.stroke!, color } }, 'strokeColor')} />
          <NumberField label={t('inspector.strokeWidth')} short="px" value={s.stroke.width} min={0} step={0.5} onChange={(width) => setS({ stroke: { ...s.stroke!, width } }, 'strokeWidth')} />
        </Row>
      )}
      <ShadowFields shadow={s.shadow} onChange={(shadow, key) => setS({ shadow }, key)} />
    </Section>
  );
}

function OverflowSection({ el, edit, fit }: { el: TextElement; edit: Edit; fit: StaticFrame['text'][number] | undefined }) {
  const o = el.overflow;
  const setO = (overflow: TextElement['overflow'], key: string) => edit((x) => (x.type === 'text' ? { ...x, overflow } : x), key);
  return (
    <Section title={t('inspector.overflow')} testId="section-overflow">
      <SelectField
        label={t('inspector.overflowMode')}
        value={o.mode}
        testId="overflow-mode"
        options={[
          { value: 'none', label: t('overflow.none') },
          { value: 'shrink', label: t('overflow.shrink') },
          { value: 'wrap', label: t('overflow.wrap') },
          { value: 'ellipsis', label: t('overflow.ellipsis') },
        ]}
        onChange={(mode) =>
          setO(mode === 'shrink' ? { mode, minFontSize: Math.max(1, Math.round(el.style.fontSize / 2)) } : mode === 'wrap' ? { mode, maxLines: 2 } : { mode }, 'overflow')
        }
      />
      {o.mode === 'shrink' && <NumberField label={t('inspector.minFontSize')} short={t('inspector.minSize')} value={o.minFontSize} min={1} max={el.style.fontSize} suffix="px" testId="min-font-size" onChange={(minFontSize) => setO({ mode: 'shrink', minFontSize }, 'minFont')} />}
      {o.mode === 'wrap' && <NumberField label={t('inspector.maxLines')} short={t('inspector.lines')} value={o.maxLines} min={1} step={1} onChange={(maxLines) => setO({ mode: 'wrap', maxLines: Math.max(1, Math.round(maxLines)) }, 'maxLines')} />}
      <p className="hint">{t(`overflow.${o.mode}.hint`)}</p>
      {fit && fit.fontSize < el.style.fontSize && <p className="hint ok">{t('inspector.shrunkTo', { size: Math.round(fit.fontSize * 10) / 10 })}</p>}
      {fit?.problem && (
        <p className="hint warn" role="status">
          {t(`problem.text.${fit.problem}`, { name: el.name })}
        </p>
      )}
    </Section>
  );
}

function ShadowFields({ shadow, onChange }: { shadow: ShapeStyle['shadow']; onChange: (s: ShapeStyle['shadow'], key: string) => void }) {
  return (
    <>
      <Toggle label={t('inspector.shadow')} checked={shadow !== undefined} onChange={(on) => onChange(on ? { color: '#00000099', x: 0, y: 4, blur: 12 } : undefined, 'shadow')} />
      {shadow && (
        <>
          <ColorField label={t('inspector.shadowColor')} value={shadow.color} onChange={(color) => onChange({ ...shadow, color }, 'shadowColor')} />
          <Row className="three">
            <NumberField label={t('inspector.shadowX')} short="X" value={shadow.x} onChange={(x) => onChange({ ...shadow, x }, 'shadowX')} />
            <NumberField label={t('inspector.shadowY')} short="Y" value={shadow.y} onChange={(y) => onChange({ ...shadow, y }, 'shadowY')} />
            <NumberField label={t('inspector.shadowBlur')} short={t('inspector.blur')} value={shadow.blur} min={0} onChange={(blur) => onChange({ ...shadow, blur }, 'shadowBlur')} />
          </Row>
        </>
      )}
    </>
  );
}

function ShapeSection({ el, edit }: { el: Element & { type: 'rect' | 'ellipse' | 'group' }; edit: Edit }) {
  const st = el.style as ShapeStyle;
  const setS = (patch: Partial<ShapeStyle>, key: string) =>
    edit((x) => {
      if (x.type !== 'rect' && x.type !== 'ellipse' && x.type !== 'group') return x;
      const style = { ...(x.style as ShapeStyle), ...patch };
      for (const k of Object.keys(style) as Array<keyof ShapeStyle>) if (style[k] === undefined) delete style[k];
      return { ...x, style } as Element;
    }, key);
  const fill = st.fill;
  const fillType = fill?.type ?? 'none';
  const radius = st.cornerRadius;
  const perCorner = Array.isArray(radius);
  const corners: [number, number, number, number] = Array.isArray(radius) ? radius : [radius ?? 0, radius ?? 0, radius ?? 0, radius ?? 0];
  return (
    <Section title={t('inspector.fillStroke')} testId="section-fill">
      <SelectField
        label={t('inspector.fill')}
        value={fillType}
        testId="fill-type"
        options={[
          { value: 'none', label: t('inspector.none') },
          { value: 'solid', label: t('fill.solid') },
          { value: 'linear-gradient', label: t('fill.gradient') },
        ]}
        onChange={(type) => {
          const base = fill?.type === 'solid' ? fill.color : fill?.type === 'linear-gradient' ? fill.stops[0]!.color : '#00B4C8';
          const next: Fill | undefined =
            type === 'solid' ? { type, color: base } : type === 'linear-gradient' ? { type, angle: 90, stops: [{ offset: 0, color: base }, { offset: 1, color: '#0B1C26' }] } : undefined;
          setS({ fill: next }, 'fill');
        }}
      />
      {fill?.type === 'solid' && <ColorField label={t('inspector.fillColor')} value={fill.color} testId="fill-color" onChange={(color) => setS({ fill: { type: 'solid', color } }, 'fillColor')} />}
      {fill?.type === 'linear-gradient' && (
        <>
          <NumberField label={t('inspector.angle')} short="°" value={fill.angle} onChange={(angle) => setS({ fill: { ...fill, angle } }, 'angle')} />
          {fill.stops.map((stop, i) => (
            <ColorField
              key={i}
              label={t('inspector.stop', { n: i + 1 })}
              value={stop.color}
              onChange={(color) => setS({ fill: { ...fill, stops: fill.stops.map((s2, j) => (j === i ? { ...s2, color } : s2)) } }, `stop${i}`)}
            />
          ))}
        </>
      )}
      <Toggle label={t('inspector.stroke')} checked={st.stroke !== undefined} onChange={(on) => setS({ stroke: on ? { color: '#FFFFFF', width: 2 } : undefined }, 'stroke')} />
      {st.stroke && (
        <Row className="two">
          <ColorField label={t('inspector.strokeColor')} value={st.stroke.color} onChange={(color) => setS({ stroke: { ...st.stroke!, color } }, 'strokeColor')} />
          <NumberField label={t('inspector.strokeWidth')} short="px" value={st.stroke.width} min={0} onChange={(width) => setS({ stroke: { ...st.stroke!, width } }, 'strokeWidth')} />
        </Row>
      )}
      {el.type !== 'ellipse' && (
        <>
          <div className="field-label sub with-action">
            {t('inspector.radius')}
            <IconButton label={perCorner ? t('inspector.radiusUniform') : t('inspector.radiusPerCorner')} pressed={perCorner} onClick={() => setS({ cornerRadius: perCorner ? corners[0] : corners }, 'radiusMode')}>
              {perCorner ? <Unlink size={12} /> : <Link2 size={12} />}
            </IconButton>
          </div>
          {perCorner ? (
            <Row className="four">
              {(['tl', 'tr', 'br', 'bl'] as const).map((c, i) => (
                <NumberField
                  key={c}
                  label={t(`inspector.radius.${c}`)}
                  short={t(`inspector.radius.${c}.short`)}
                  value={corners[i]}
                  min={0}
                  testId={`radius-${c}`}
                  onChange={(v) => setS({ cornerRadius: corners.map((r, j) => (j === i ? v : r)) as [number, number, number, number] }, `radius${i}`)}
                />
              ))}
            </Row>
          ) : (
            <NumberField label={t('inspector.radius')} short="r" value={corners[0]} min={0} testId="radius" onChange={(v) => setS({ cornerRadius: v || undefined }, 'radius')} />
          )}
        </>
      )}
      <ShadowFields shadow={st.shadow} onChange={(shadow, key) => setS({ shadow }, key)} />
      <NumberField label={t('inspector.blur')} short={t('inspector.blur')} value={st.blur ?? 0} min={0} suffix="px" onChange={(blur) => setS({ blur: blur || undefined }, 'blur')} />
      {(st.blur ?? 0) > 0 && <p className="hint warn">{t('inspector.blurWarning')}</p>}
    </Section>
  );
}

function LineSection({ el, edit }: { el: Element & { type: 'line' }; edit: Edit }) {
  return (
    <Section title={t('inspector.line')}>
      <ColorField label={t('inspector.color')} value={el.stroke.color} onChange={(color) => edit((x) => (x.type === 'line' ? { ...x, stroke: { ...x.stroke, color } } : x), 'color')} />
      <SelectField
        label={t('inspector.cap')}
        value={el.cap}
        options={[
          { value: 'butt', label: t('cap.butt') },
          { value: 'round', label: t('cap.round') },
        ]}
        onChange={(cap) => edit((x) => (x.type === 'line' ? { ...x, cap } : x), 'cap')}
      />
    </Section>
  );
}

function MediaSection({ el, edit, store }: { el: Element & { type: 'image' | 'video' }; edit: Edit; store: EditorStore }) {
  useSyncExternalStore(assetCache.subscribe, assetCache.getVersion);
  const items = assetCache.library.filter((a) => a.kind === el.type);
  return (
    <Section title={el.type === 'image' ? t('type.image') : t('type.video')}>
      <SelectField
        label={t('inspector.source')}
        value={el.asset}
        options={items.map((a) => ({ value: a.hash, label: a.originalName }))}
        onChange={(hash) => {
          const a = assetCache.byHash(hash);
          if (!a) return;
          store.apply((s) => pruneAssets(patchElement(ensureAsset(s, a.sceneAsset), el.id, (x) => (x.type === 'image' || x.type === 'video' ? ({ ...x, asset: hash } as Element) : x))));
        }}
      />
      <SelectField
        label={t('inspector.fit')}
        value={el.fit}
        options={[
          { value: 'contain', label: t('fit.contain') },
          { value: 'cover', label: t('fit.cover') },
          { value: 'fill', label: t('fit.fill') },
        ]}
        onChange={(fit) => edit((x) => (x.type === 'image' || x.type === 'video' ? ({ ...x, fit } as Element) : x), 'fit')}
      />
      <OverrideReset store={store} elementId={el.id} keys={(k) => k === 'fit'} testId="fit" />
      {el.type === 'video' && (
        <>
          <Toggle label={t('inspector.loop')} checked={el.loop} onChange={(loop) => edit((x) => (x.type === 'video' ? { ...x, loop } : x), 'loop')} />
          <VideoInfo hash={el.asset} />
        </>
      )}
    </Section>
  );
}

const EXPOSE_LABEL: Record<Exposable, string> = {
  text: 'expose.text',
  color: 'expose.color',
  strokeColor: 'expose.strokeColor',
  visible: 'expose.visible',
  image: 'expose.image',
  opacity: 'expose.opacity',
};

function FieldsSection({ el, store }: { el: Element; store: EditorStore }) {
  const scene = useEditor(store, sceneView);
  const props = exposableProperties(el);
  return (
    <Section title={t('inspector.fields')} testId="section-fields">
      <p className="hint">{t('inspector.fieldsHint')}</p>
      <ul className="expose-list">
        {props.map((p) => {
          const f = fieldFor(scene, el.id, p);
          return (
            <li key={p}>
              <span>{t(EXPOSE_LABEL[p])}</span>
              {f ? (
                <>
                  <button type="button" className="chip" onClick={() => store.setUi({ rightTab: 'data' })} title={t('inspector.openField')} data-testid={`field-chip-${p}`}>
                    {f.title}
                  </button>
                  <button type="button" className="btn small ghost" onClick={() => store.apply((s) => unexposeField(s, el.id, p))} data-testid={`unexpose-${p}`}>
                    {t('inspector.unexpose')}
                  </button>
                </>
              ) : (
                <button type="button" className="btn small" onClick={() => expose(store, el.id, p)} data-testid={`expose-${p}`}>
                  {t('inspector.expose')}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

function VideoInfo({ hash }: { hash: string }) {
  const a = assetCache.byHash(hash);
  if (!a) return null;
  const text = a.mime === 'video/mp4' ? t('inspector.video.mp4') : a.meta.alpha ? t('inspector.video.alpha') : t('inspector.video.opaque');
  return (
    <p className={`hint${a.mime === 'video/mp4' ? ' warn' : ''}`} data-testid="video-info">
      {text} {a.meta.width && a.meta.height ? `${a.meta.width} × ${a.meta.height}.` : ''} {t('inspector.video.timing')}
    </p>
  );
}

function EffectsSection({ el, edit }: { el: Element; edit: Edit }) {
  const mode = el.blendMode ?? 'normal';
  return (
    <Section title={t('inspector.effects')} testId="section-effects">
      <SelectField
        label={t('inspector.blendMode')}
        value={mode}
        testId="blend-mode"
        options={BLEND_MODES.map((m) => ({ value: m, label: t(`blend.${m}`) }))}
        onChange={(m) =>
          edit((x) => {
            const { blendMode: _b, ...rest } = x;
            return (m === 'normal' ? rest : { ...rest, blendMode: m }) as Element;
          }, 'blend')
        }
      />
      {mode !== 'normal' && <p className="hint warn">{t('inspector.blendWarning')}</p>}
    </Section>
  );
}
